import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAppLogo } from '../hooks/useAppLogo';
import '../styles/AuthPages.css';

const APP_LOGO = `${process.env.PUBLIC_URL || ''}/logo.png`;

// Detect system theme for auth pages
const getSystemTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'day';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
};

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getSystemTheme);
  const { logoUrl: appLogoUrl } = useAppLogo(theme);
  const logoUrl = APP_LOGO || appLogoUrl;
  
  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setTheme(e.matches ? 'night' : 'day');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Single source of truth for demo credentials on the frontend.
  // Can be overridden via env vars; otherwise we fall back to the known pair.
  const DEMO_USERNAME = process.env.REACT_APP_DEMO_USERNAME || 'demo';
  const DEMO_PASSWORD = process.env.REACT_APP_DEMO_PASSWORD || 'demo123';

  const demoDefaults = useMemo(() => {
    const globalWindow = typeof window !== 'undefined' ? window : {};
    const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const urlDemo = urlParams?.get('demo') === 'true';
    const enabled = Boolean(globalWindow.__DEMO_MODE__ || urlDemo);
    const tooltip =
      globalWindow.__DEMO_TOOLTIP__ ||
      process.env.REACT_APP_DEMO_TOOLTIP ||
      'Live demo is read-only and refreshes nightly.';
    return {
      enabled,
      username: globalWindow.__DEMO_USERNAME__ || DEMO_USERNAME,
      password: globalWindow.__DEMO_PASSWORD__ || DEMO_PASSWORD,
      tooltip
    };
  }, [DEMO_USERNAME, DEMO_PASSWORD]);
  const demoAvailable = demoDefaults.enabled && demoDefaults.username;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login(username, password);
      onLogin(response.data.user, response.data.requires_setup);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    if (!demoDefaults.enabled) return;
    if (!demoAvailable) {
      setError('Demo login is not configured yet.');
      return;
    }

    setError('');
    setLoading(true);
    const demoPassword = demoDefaults.password || 'demo-login';
    setUsername(demoDefaults.username);
    setPassword(demoPassword);

    try {
      const response = await authAPI.login(demoDefaults.username, demoPassword, { demo: true });
      onLogin(response.data.user, response.data.requires_setup);
    } catch (err) {
      setError(err.response?.data?.error || 'Demo login is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`auth-page theme-${theme}`}>
      <div className="auth-container">
        <div className="auth-logo">
          {logoUrl && (
            <div className="auth-logo-shell">
              <div className="auth-logo-glow"></div>
              <img src={logoUrl} alt="Frame-Book logo" className="auth-logo-image" />
            </div>
          )}
          <h1>Frame-Book</h1>
          <p>Bike Passport & Service History</p>
        </div>

        <div className="auth-card">
          <h2>Welcome Back</h2>
          
          {error && <div className="error">{error}</div>}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Username or Email</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your username or email"
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
            
            <div className="forgot-password-link" style={{ textAlign: 'center', marginTop: '15px' }}>
              <Link to="/forgot-password" style={{ color: '#667eea', fontSize: '14px' }}>
                Forgot your password?
              </Link>
            </div>
          </form>

          {demoDefaults.enabled && (
            <div className="demo-login">
              <button
                type="button"
                className="btn btn-secondary btn-block"
                onClick={handleDemoLogin}
                disabled={loading || !demoAvailable}
              >
                {loading ? 'Preparing demo…' : demoAvailable ? 'Try Live Demo' : 'Demo Unavailable'}
              </button>
              {demoAvailable && (
                <p className="demo-credentials">
                  Demo login:&nbsp;
                  <code>{demoDefaults.username}</code>
                  {' / '}
                  <code>{demoDefaults.password || 'demo-login'}</code>
                </p>
              )}
              <p className="demo-note">{demoDefaults.tooltip}</p>
            </div>
          )}

          <div className="auth-footer">
            <p>Don't have an account? <Link to="/onboarding">Register here</Link></p>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Link to="/" style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none' }}>
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;

